import React, { createContext, useContext, ReactNode, useMemo } from "react";
import { useUserCompanies, UserCompany } from "@/hooks/useUserCompanies";
import { useAuthContext } from "./AuthContext";

interface CompanyDataContextType {
  companies: UserCompany[];
  isLoading: boolean;
  hasMultipleCompanies: boolean;
}

const CompanyDataContext = createContext<CompanyDataContextType | undefined>(undefined);

export function CompanyDataProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthContext();
  const { data: companiesData, isLoading } = useUserCompanies(user?.id || null);
  const companies = companiesData?.verified || [];

  const value = useMemo<CompanyDataContextType>(
    () => ({
      companies,
      isLoading,
      hasMultipleCompanies: companies.length > 1,
    }),
    [companies, isLoading]
  );

  return (
    <CompanyDataContext.Provider value={value}>
      {children}
    </CompanyDataContext.Provider>
  );
}

export function useCompanyData() {
  const context = useContext(CompanyDataContext);
  if (context === undefined) {
    throw new Error("useCompanyData must be used within CompanyDataProvider");
  }
  return context;
}
